import {
  ChevronDown,
  ChevronUp,
  Eye,
  HelpCircle,
  Loader2,
  Users,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Id } from "@cvx/_generated/dataModel";
import PlatformIcon from "../../Generic/PlatformIcon";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { api } from "@cvx/_generated/api";
import { formatNumber } from "@/utils/format";
import { useAccount } from "@/state/useAccount";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";

export default function MetaDashboard() {
  const navigate = useNavigate();
  const { setAppId } = useAccount();
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(
    new Set(),
  );
  const hasInitializedRef = useRef(false);

  // Get current user to check if they're a CreatorGrid team member
  const user = useQuery(api.app.getCurrentUser, {});
  const isCreatorGridTeam = user ? TEAM_USER_IDS.includes(user._id) : false;

  // Get aggregate stats across all apps
  const aggregateStats = useQuery(
    api.dashboard.getAllAppsCampaignsAggregateStats,
    {},
  );

  // Expand all campaigns with top performers by default (only once on initial load)
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      aggregateStats?.campaignStats &&
      aggregateStats.campaignStats.length > 0
    ) {
      const campaignsWithTopPerformers = new Set<string>();
      for (const campaignStat of aggregateStats.campaignStats) {
        if (
          campaignStat.topPerformers &&
          campaignStat.topPerformers.length > 0
        ) {
          campaignsWithTopPerformers.add(campaignStat.campaignId);
        }
      }
      if (campaignsWithTopPerformers.size > 0) {
        setExpandedCampaigns(campaignsWithTopPerformers);
      }
      hasInitializedRef.current = true;
    }
  }, [aggregateStats?.campaignStats]);

  const toggleTopPerformers = (e: React.MouseEvent, campaignId: string) => {
    e.stopPropagation();
    setExpandedCampaigns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  const handleCampaignClick = (
    appId: Id<"apps">,
    campaignId: Id<"campaigns">,
  ) => {
    setAppId(appId);
    navigate({
      to: "/dashboard",
      search: { campaignId: campaignId as string },
    });
  };

  if (!aggregateStats) {
    return (
      <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="animate-spin mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        {/* Stats Cards Section - Only visible for CreatorGrid team members */}
        {isCreatorGridTeam && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Total Views Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Views
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {formatNumber(aggregateStats.totalViews ?? 0)}
                </p>
                <p className="text-xs text-primary/60">Across all campaigns</p>
              </div>
            </div>

            {/* Total Videos by Platform Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Videos Posted
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {Object.values(aggregateStats.videosByPlatform ?? {}).reduce(
                    (sum, count) => sum + count,
                    0,
                  )}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {aggregateStats.videosByPlatform &&
                    Object.entries(aggregateStats.videosByPlatform).map(
                      ([platform, count]) => (
                        <div
                          key={platform}
                          className="flex items-center gap-1.5 px-2 py-1 bg-secondary rounded-md"
                        >
                          <PlatformIcon platform={platform} />
                          <span className="text-xs text-primary/60">
                            {formatNumber(count)}
                          </span>
                        </div>
                      ),
                    )}
                </div>
              </div>
            </div>

            {/* Unique Creators by Campaign Type Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-primary/60">
                    Total Unique Creators
                  </h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-primary/40 hover:text-primary/60 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs" side="bottom">
                        <p className="mb-2 font-medium">
                          Total Unique Creators (Deduplicated)
                        </p>
                        <p className="text-sm">
                          The total counts each creator only once, even if they
                          participate in multiple campaign types. The breakdown
                          below shows how many unique creators are in each type,
                          but the same creator may appear in multiple
                          categories.
                        </p>
                        <p className="text-sm mt-2 text-primary/60">
                          Example: If 1 creator is in both CiR and Challenge
                          campaigns, the total is 1, but the breakdown shows 1
                          in each category.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Users className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {aggregateStats.totalUniqueCreators ?? 0}
                </p>
                <div className="flex flex-col gap-1.5 mt-2">
                  {aggregateStats.uniqueCreatorsByType && (
                    <>
                      {aggregateStats.uniqueCreatorsByType.CiR > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">
                            Creator in Residence:
                          </span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.CiR}
                          </span>
                        </div>
                      )}
                      {aggregateStats.uniqueCreatorsByType.influencer > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">Influencer:</span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.influencer}
                          </span>
                        </div>
                      )}
                      {aggregateStats.uniqueCreatorsByType.challenge > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">Challenge:</span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.challenge}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Top Performing Content Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-primary/60">
                  Top Performing Content
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="mt-2 mb-4">
                {aggregateStats.topContent &&
                aggregateStats.topContent.length > 0 ? (
                  <div className="space-y-2">
                    {aggregateStats.topContent.map((content) => (
                      <div
                        key={content.videoId}
                        className="flex items-center justify-between text-sm"
                      >
                        <button
                          onClick={() => {
                            setAppId(content.appId as Id<"apps">);
                            navigate({
                              to: "/dashboard",
                              search: {
                                campaignId: content.campaignId,
                              },
                            });
                          }}
                          className="text-primary hover:underline truncate flex-1 mr-2 text-left cursor-pointer"
                          title={content.videoTitle}
                        >
                          {content.videoTitle}
                        </button>
                        <span className="font-semibold text-primary">
                          {formatNumber(content.views)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-3xl font-semibold text-primary">
                    No videos
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Campaign Overviews Section */}
        <div className="rounded-lg border border-border bg-card">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-lg font-semibold text-primary">
              Campaign Overviews
            </h2>
            <p className="text-sm text-primary/60">
              Sort by top performing campaigns
            </p>
          </div>
          <div className="overflow-x-auto">
            {aggregateStats.campaignStats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="mb-4 text-lg text-primary/60">
                  No campaigns found
                </p>
                <p className="text-sm text-primary/60">
                  Create campaigns to see performance metrics
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {aggregateStats.campaignStats.map((campaignStat) => {
                  const topPerformers = campaignStat.topPerformers ?? [];
                  const isExpanded = expandedCampaigns.has(
                    campaignStat.campaignId,
                  );

                  return (
                    <div
                      key={campaignStat.campaignId}
                      className="p-6 hover:bg-secondary/30 transition-colors"
                    >
                      {/* Campaign Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          {topPerformers.length > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 hover:bg-secondary"
                              onClick={(e) =>
                                toggleTopPerformers(e, campaignStat.campaignId)
                              }
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <div>
                            <h3 className="text-lg font-semibold text-primary">
                              {campaignStat.campaignName}
                            </h3>
                            <p className="text-sm text-primary/60">
                              {campaignStat.appName}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleCampaignClick(
                              campaignStat.appId as Id<"apps">,
                              campaignStat.campaignId as Id<"campaigns">,
                            )
                          }
                        >
                          View Details
                        </Button>
                      </div>

                      {/* Campaign Stats Grid */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        {/* Total Views */}
                        <div className="border border-border rounded-lg p-4">
                          <div className="text-xs text-primary/60 mb-1">
                            Total Views
                          </div>
                          <div className="text-2xl font-semibold text-primary">
                            {formatNumber(campaignStat.totalViews)}
                          </div>
                        </div>

                        {/* Total Videos */}
                        <div className="border border-border rounded-lg p-4">
                          <div className="text-xs text-primary/60 mb-1">
                            Total Videos
                          </div>
                          <div className="text-2xl font-semibold text-primary">
                            {formatNumber(campaignStat.totalVideos)}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {campaignStat.videosByPlatform &&
                              Object.entries(campaignStat.videosByPlatform).map(
                                ([platform, count]) => (
                                  <div
                                    key={platform}
                                    className="flex items-center gap-1 text-xs"
                                  >
                                    <PlatformIcon platform={platform} />
                                    <span className="text-primary/60">
                                      {count}
                                    </span>
                                  </div>
                                ),
                              )}
                          </div>
                        </div>

                        {/* Creators */}
                        <div className="border border-border rounded-lg p-4">
                          <div className="text-xs text-primary/60 mb-1">
                            Creators
                          </div>
                          <div className="text-2xl font-semibold text-primary">
                            {formatNumber(campaignStat.uniqueCreators)}
                          </div>
                        </div>
                      </div>

                      {/* Top Performers */}
                      {topPerformers.length > 0 && isExpanded && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs font-medium text-primary/60 uppercase tracking-wide">
                              Top Performers:
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-4">
                            {topPerformers.map((performer, index) => (
                              <div
                                key={performer.creatorId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate({
                                    to: "/creators/$creatorId",
                                    params: {
                                      creatorId: performer.creatorId,
                                    },
                                  });
                                }}
                                className="flex items-center gap-3 bg-secondary border border-border rounded-lg px-4 py-3 min-w-[200px] cursor-pointer hover:bg-secondary/50 transition-colors hover:border-primary/20"
                              >
                                <div className="flex-shrink-0">
                                  <span className="text-sm font-bold text-primary/40 mr-2">
                                    #{index + 1}
                                  </span>
                                  <img
                                    src={
                                      performer.avatarUrl ||
                                      "/default-avatar.png"
                                    }
                                    alt={performer.name}
                                    className="h-10 w-10 rounded-full object-cover inline-block"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm text-primary truncate">
                                    {performer.name}
                                  </p>
                                  <div className="flex gap-3 mt-1 text-xs text-primary/60">
                                    <span>
                                      {formatNumber(performer.totalViews)} views
                                    </span>
                                    <span>
                                      {formatNumber(performer.totalLikes)} likes
                                    </span>
                                    <span>{performer.totalVideos} videos</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
